import {
  FormControl,
  InputLabel,
  Typography,
  Box,
  Chip,
  SxProps,
  Theme,
} from '@mui/material';
import { BarChart, Bolt, RateReview } from '@mui/icons-material';
import { FC, useMemo } from 'react';
import {
  ArticleFormat,
  BrandKey,
  UpsertArticleMutation_mutation_upsertArticle_Article_qualifiers_ArticleQualifier,
  GetQualifiersQuery_query_qualifiers_Qualifier,
  QualifierOrderingInput,
  QualifierStatus,
  QualifierType,
} from '../../../__generated__/queries-web';
import { useQualifiers } from './getQualifers.web.graphql';
import { useStyles } from './styles';

interface QualifierTypeConfig {
  type: QualifierType;
  Icon: typeof BarChart;
  main: string;
  hover: string;
  iconSx?: SxProps<Theme>;
}

const QUALIFIER_TYPE_CONFIG: readonly QualifierTypeConfig[] = [
  {
    type: QualifierType.Trigger,
    Icon: Bolt,
    main: 'rgb(102, 108, 255)',
    hover: 'rgb(82, 86, 204)',
  },
  {
    type: QualifierType.IntroAvis,
    Icon: RateReview,
    main: 'rgb(88, 140, 180)',
    hover: 'rgb(70, 112, 144)',
    iconSx: { paddingLeft: '1.5px' },
  },
  {
    type: QualifierType.Statistical,
    Icon: BarChart,
    main: 'rgb(13, 148, 136)',
    hover: 'rgb(10, 118, 109)',
  },
];

interface QualifierChipProps {
  qualifier: GetQualifiersQuery_query_qualifiers_Qualifier;
  config: QualifierTypeConfig;
  selected: boolean;
  onToggle: () => void;
}

const QualifierChip: FC<QualifierChipProps> = ({
  qualifier,
  config,
  selected,
  onToggle,
}) => {
  const { Icon, main, hover, iconSx } = config;
  return (
    <Chip
      icon={
        <Icon
          sx={{
            fontSize: '18px !important',
            color: selected ? 'white' : 'inherit',
            ...iconSx,
          }}
        />
      }
      label={qualifier.title}
      onClick={onToggle}
      sx={{
        cursor: 'pointer',
        border: '1px solid',
        borderColor: selected ? main : 'grey.400',
        backgroundColor: selected ? main : 'grey.200',
        color: selected ? 'white' : 'grey.600',
        '& .MuiChip-icon': {
          color: selected ? 'white !important' : 'grey.500',
        },
        '&:hover': {
          backgroundColor: selected ? hover : 'grey.300',
        },
        transition: 'all 0.2s',
      }}
    />
  );
};

interface QualifiersProps {
  brandKey: BrandKey;
  qualifiers: UpsertArticleMutation_mutation_upsertArticle_Article_qualifiers_ArticleQualifier[];
  onChange: (
    updatedQualifiers: UpsertArticleMutation_mutation_upsertArticle_Article_qualifiers_ArticleQualifier[],
  ) => void;
  articleFormat: ArticleFormat;
  label?: string;
}

export const Qualifiers: FC<QualifiersProps> = ({
  brandKey,
  qualifiers,
  onChange,
  articleFormat,
  label,
}) => {
  const classes = useStyles();
  const { data } = useQualifiers({
    first: 100,
    where: { brandKey, format: articleFormat },
    orderBy: [QualifierOrderingInput.title_ASC],
  });

  const publishedQualifiers = data?.qualifiers?.filter(
    (qualifier: GetQualifiersQuery_query_qualifiers_Qualifier) =>
      qualifier.status !== QualifierStatus.Draft,
  );

  const isChecked = (id: string) =>
    qualifiers.some(({ qualifier }) => qualifier.id === id);

  const qualifiersByType = useMemo(() => {
    type Acc = Partial<
      Record<QualifierType, GetQualifiersQuery_query_qualifiers_Qualifier[]>
    >;
    return (publishedQualifiers ?? []).reduce<Acc>(
      (acc, qualifier) => ({
        ...acc,
        [qualifier.type]: [...(acc[qualifier.type] ?? []), qualifier],
      }),
      {},
    );
  }, [publishedQualifiers]);

  const handleToggle = (
    selectedQualifier: GetQualifiersQuery_query_qualifiers_Qualifier,
  ) => {
    const isSelected = isChecked(selectedQualifier.id);
    const updatedQualifiers = isSelected
      ? qualifiers.filter(
          ({ qualifier }) => qualifier.id !== selectedQualifier.id,
        )
      : [
          ...qualifiers,
          ({
            qualifier: { ...selectedQualifier },
            __typename: 'ArticleQualifier',
          } as unknown) as UpsertArticleMutation_mutation_upsertArticle_Article_qualifiers_ArticleQualifier,
        ];
    onChange(updatedQualifiers);
  };

  if (!publishedQualifiers?.length) return null;

  return (
    <FormControl className={classes.formSuggest} variant="standard">
      {label ? (
        <Typography className={classes.subTitle} component="h3">
          {label}
        </Typography>
      ) : (
        <InputLabel shrink>Qualifiers :</InputLabel>
      )}
      <Box
        sx={{
          backgroundColor: 'white',
          borderRadius: '5px',
          padding: 2,
          marginTop: 2,
        }}
      >
        {QUALIFIER_TYPE_CONFIG.map((config) => {
          const list = qualifiersByType[config.type] ?? [];
          if (!list.length) return null;
          return (
            <Box
              key={config.type}
              sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}
            >
              {list.map((qualifier) => (
                <QualifierChip
                  key={qualifier.id}
                  qualifier={qualifier}
                  config={config}
                  selected={isChecked(qualifier.id)}
                  onToggle={() => handleToggle(qualifier)}
                />
              ))}
            </Box>
          );
        })}
      </Box>
    </FormControl>
  );
};
